import { useState, useEffect } from 'react';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Welcome Graphic Component
 * 
 * Shows a one-time welcome message/graphic when users first visit a page.
 * Uses localStorage to track which pages have been seen.
 * 
 * @param {string} pageKey - Unique identifier for the page (e.g., 'overview', 'kb', 'analytics')
 * @param {string} title - Main title text
 * @param {string} subtitle - Subtitle/description text
 * @param {string} illustration - Optional illustration type: 'analytics', 'documents', 'team', 'chat', 'settings'
 * @param {string} ctaText - Call-to-action button text
 * @param {function} onCtaClick - Callback when CTA is clicked
 * @param {function} onDismiss - Callback when dismissed
 * @param {string} variant - Style variant: 'default', 'compact', 'hero'
 */
export function WelcomeGraphic({
  pageKey,
  title,
  subtitle,
  illustration = 'default',
  ctaText,
  onCtaClick,
  onDismiss,
  variant = 'default',
  className,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const storageKey = `eva_welcome_seen_${pageKey}`;
  
  useEffect(() => {
    // Check if user has seen this welcome graphic before
    const hasSeen = localStorage.getItem(storageKey);
    if (!hasSeen) {
      setIsAnimating(true);
      // Small delay for entrance animation
      setTimeout(() => setIsVisible(true), 100);
    }
  }, [storageKey]);
  
  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      localStorage.setItem(storageKey, 'true');
      setIsVisible(false);
      onDismiss?.();
    }, 300);
  };
  
  const handleCta = () => {
    handleDismiss();
    onCtaClick?.();
  };
  
  if (!isVisible) return null;
  
  // SVG Illustrations for different contexts
  const illustrations = {
    analytics: (
      <svg viewBox="0 0 200 160" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="20" y="100" width="30" height="50" rx="4" className="fill-primary/20" />
        <rect x="60" y="70" width="30" height="80" rx="4" className="fill-primary/40" />
        <rect x="100" y="40" width="30" height="110" rx="4" className="fill-primary/60" />
        <rect x="140" y="60" width="30" height="90" rx="4" className="fill-primary/80" />
        <path d="M35 90 L75 60 L115 30 L155 50" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round" />
        <circle cx="35" cy="90" r="6" className="fill-primary" />
        <circle cx="75" cy="60" r="6" className="fill-primary" />
        <circle cx="115" cy="30" r="6" className="fill-primary" />
        <circle cx="155" cy="50" r="6" className="fill-primary" />
      </svg>
    ),
    documents: (
      <svg viewBox="0 0 200 160" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="20" width="100" height="120" rx="8" className="fill-muted stroke-border" strokeWidth="2" />
        <rect x="45" y="40" width="70" height="8" rx="2" className="fill-primary/30" />
        <rect x="45" y="55" width="50" height="6" rx="2" className="fill-muted-foreground/20" />
        <rect x="45" y="68" width="60" height="6" rx="2" className="fill-muted-foreground/20" />
        <rect x="45" y="81" width="45" height="6" rx="2" className="fill-muted-foreground/20" />
        <rect x="45" y="100" width="70" height="25" rx="4" className="fill-primary/10 stroke-primary/30" strokeWidth="1" />
        <path d="M140 60 L170 60 L170 140 L80 140 L80 120" stroke="hsl(var(--border))" strokeWidth="2" strokeDasharray="4 4" />
        <circle cx="150" cy="40" r="25" className="fill-primary/10 stroke-primary" strokeWidth="2" />
        <path d="M142 40 L148 46 L160 34" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    team: (
      <svg viewBox="0 0 200 160" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="50" r="25" className="fill-primary/20 stroke-primary" strokeWidth="2" />
        <circle cx="100" cy="42" r="10" className="fill-primary/40" />
        <path d="M80 65 Q100 80 120 65" className="fill-primary/20" />
        <circle cx="50" cy="80" r="18" className="fill-muted stroke-border" strokeWidth="2" />
        <circle cx="50" cy="74" r="7" className="fill-muted-foreground/30" />
        <path d="M35 92 Q50 102 65 92" className="fill-muted" />
        <circle cx="150" cy="80" r="18" className="fill-muted stroke-border" strokeWidth="2" />
        <circle cx="150" cy="74" r="7" className="fill-muted-foreground/30" />
        <path d="M135 92 Q150 102 165 92" className="fill-muted" />
        <path d="M68 85 L82 70" stroke="hsl(var(--primary))" strokeWidth="2" strokeDasharray="4 4" />
        <path d="M132 85 L118 70" stroke="hsl(var(--primary))" strokeWidth="2" strokeDasharray="4 4" />
        <rect x="60" y="120" width="80" height="25" rx="12" className="fill-primary/10 stroke-primary/30" strokeWidth="1" />
        <text x="100" y="137" textAnchor="middle" className="fill-primary text-xs font-medium">Collaborate</text>
      </svg>
    ),
    chat: (
      <svg viewBox="0 0 200 160" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="30" width="100" height="70" rx="12" className="fill-muted stroke-border" strokeWidth="2" />
        <polygon points="50,100 60,100 55,115" className="fill-muted stroke-border" strokeWidth="2" />
        <rect x="45" y="50" width="60" height="6" rx="2" className="fill-muted-foreground/30" />
        <rect x="45" y="62" width="45" height="6" rx="2" className="fill-muted-foreground/20" />
        <rect x="45" y="74" width="50" height="6" rx="2" className="fill-muted-foreground/20" />
        <rect x="90" y="60" width="90" height="60" rx="12" className="fill-primary/10 stroke-primary" strokeWidth="2" />
        <polygon points="170,120 160,120 165,135" className="fill-primary/10 stroke-primary" strokeWidth="2" />
        <circle cx="115" cy="85" r="4" className="fill-primary animate-pulse" />
        <circle cx="130" cy="85" r="4" className="fill-primary animate-pulse" style={{ animationDelay: '0.2s' }} />
        <circle cx="145" cy="85" r="4" className="fill-primary animate-pulse" style={{ animationDelay: '0.4s' }} />
        <text x="130" y="105" textAnchor="middle" className="fill-primary/80 text-xs">Eva is typing...</text>
      </svg>
    ),
    settings: (
      <svg viewBox="0 0 200 160" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="80" r="40" className="fill-muted stroke-border" strokeWidth="2" />
        <circle cx="100" cy="80" r="25" className="fill-background stroke-primary" strokeWidth="2" />
        <circle cx="100" cy="80" r="10" className="fill-primary/20" />
        <g className="fill-muted-foreground/50">
          <rect x="95" y="25" width="10" height="20" rx="3" />
          <rect x="95" y="115" width="10" height="20" rx="3" />
          <rect x="140" y="75" width="20" height="10" rx="3" />
          <rect x="40" y="75" width="20" height="10" rx="3" />
          <rect x="130" y="40" width="15" height="10" rx="3" transform="rotate(45 137 45)" />
          <rect x="55" y="115" width="15" height="10" rx="3" transform="rotate(45 62 120)" />
          <rect x="130" y="110" width="15" height="10" rx="3" transform="rotate(-45 137 115)" />
          <rect x="55" y="35" width="15" height="10" rx="3" transform="rotate(-45 62 40)" />
        </g>
      </svg>
    ),
    default: (
      <svg viewBox="0 0 200 160" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="80" r="50" className="fill-primary/10 stroke-primary/30" strokeWidth="2" />
        <path d="M70 80 L90 100 L130 60" stroke="hsl(var(--primary))" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="40" cy="40" r="8" className="fill-primary/30 animate-pulse" />
        <circle cx="160" cy="50" r="6" className="fill-primary/20 animate-pulse" style={{ animationDelay: '0.5s' }} />
        <circle cx="155" cy="120" r="10" className="fill-primary/25 animate-pulse" style={{ animationDelay: '1s' }} />
        <circle cx="50" cy="130" r="5" className="fill-primary/15 animate-pulse" style={{ animationDelay: '1.5s' }} />
      </svg>
    ),
  };
  
  const variantStyles = {
    default: 'p-6 rounded-xl',
    compact: 'p-4 rounded-lg',
    hero: 'p-8 rounded-2xl',
  };
  
  return (
    <div
      className={cn(
        'relative bg-gradient-to-br from-primary/5 via-background to-primary/10 border border-primary/20',
        'transition-all duration-300 ease-out',
        isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
        variantStyles[variant],
        className
      )}
      data-testid={`welcome-graphic-${pageKey}`}
    >
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted/50 transition-colors"
        aria-label="Dismiss welcome message"
      >
        <X className="w-4 h-4 text-muted-foreground" />
      </button>
      
      <div className={cn(
        'flex gap-6',
        variant === 'hero' ? 'flex-col items-center text-center' : 'items-start'
      )}>
        {/* Illustration */}
        <div className={cn(
          'flex-shrink-0',
          variant === 'hero' ? 'w-48 h-40' : 'w-32 h-28'
        )}>
          {illustrations[illustration] || illustrations.default}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-primary uppercase tracking-wide">Welcome</span>
          </div>
          
          <h3 className={cn(
            'font-semibold text-foreground mb-2',
            variant === 'hero' ? 'text-2xl' : 'text-lg'
          )}>
            {title}
          </h3>
          
          <p className={cn(
            'text-muted-foreground mb-4',
            variant === 'hero' ? 'text-base' : 'text-sm'
          )}>
            {subtitle}
          </p>
          
          {ctaText && (
            <Button
              onClick={handleCta}
              size={variant === 'hero' ? 'default' : 'sm'}
              className="group"
              data-testid={`welcome-cta-${pageKey}`}
            >
              {ctaText}
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/5 to-transparent rounded-tr-xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-primary/5 to-transparent rounded-bl-xl pointer-events-none" />
    </div>
  );
}

/**
 * Hook to manage welcome graphic visibility
 */
export function useWelcomeGraphic(pageKey) {
  const storageKey = `eva_welcome_seen_${pageKey}`;
  // Default to false (not seen) so the welcome shows on first render
  // then useEffect will update if localStorage says it was seen
  const [hasBeenSeen, setHasBeenSeen] = useState(() => {
    // Check localStorage synchronously during initialization
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem(storageKey);
    }
    return false;
  });
  
  const markAsSeen = () => {
    localStorage.setItem(storageKey, 'true');
    setHasBeenSeen(true);
  };
  
  const reset = () => {
    localStorage.removeItem(storageKey);
    setHasBeenSeen(false);
  };
  
  return { hasBeenSeen, markAsSeen, reset };
}

/**
 * Pre-configured welcome graphics for common pages
 */
export const welcomeConfigs = {
  overview: {
    pageKey: 'overview',
    title: 'Welcome to Eva Insights',
    subtitle: 'Review workspace activity, answer feedback and knowledge coverage. Take a short tour of this page to get started.',
    illustration: 'analytics',
    ctaText: 'Take a Tour',
  },
  kb: {
    pageKey: 'kb',
    title: 'Build Your Knowledge Base',
    subtitle: 'Upload and review documents for your workspace. Eva retrieves relevant content from published, accessible documents to help answer questions.',
    illustration: 'documents',
    ctaText: 'Upload First Document',
  },
  analytics: {
    pageKey: 'analytics',
    title: 'Understand Employee Needs',
    subtitle: 'Review recorded conversations, topic trends and answer feedback. Use the underlying questions to decide what needs attention.',
    illustration: 'analytics',
    ctaText: 'Explore Analytics',
  },
  team: {
    pageKey: 'team',
    title: 'Collaborate with Your Team',
    subtitle: 'Invite colleagues to Insights and select their roles. Review the permissions table before granting access.',
    illustration: 'team',
    ctaText: 'Invite Team Member',
  },
  eva: {
    pageKey: 'eva',
    title: 'Meet Eva, Your AI Assistant',
    subtitle: 'Ask about content available in your workspace, open the sources and leave feedback on the answer.',
    illustration: 'chat',
    ctaText: 'Chat with Eva',
  },
  settings: {
    pageKey: 'settings',
    title: 'Customize Your Experience',
    subtitle: 'Review your organization’s details, branding and preferences. Check the result after saving changes.',
    illustration: 'settings',
    ctaText: 'Start Customizing',
  },
};

export default WelcomeGraphic;
